import { useCallback } from 'react';

import { useAppStore, getAppStore } from '@/store';
import openRegistrationModal from '@/utils/user/openRegistrationModal';
import { isOnboardingSession } from '@/utils/environment/onboardingSession';
import useAuth from '../useAuth';

import {
  saveGeneration,
  clearGeneration,
} from '@/utils/storage/generationStorage';
import uploadMedia from '@/utils/generation/uploadMedia';
import handleGenerationError from '@/utils/generation/handleGenerationError';
import useGenerationSubmit from './useGenerationSubmit';
import useGenerationPolling from './useGenerationPolling';
import sendGenerateStartAnalytics from '@/utils/analytics/sendGenerateStartAnalytics';
import sendGenerateSuccessAnalytics from '@/utils/analytics/sendGenerateSuccessAnalytics';

import type { IAppStore } from '@/store';

import buildParamsFromInputs from '@/utils/flow/buildParamsFromInputs';
import type { ContentFile, WebhookContext } from '@/types';

const selector = (store: IAppStore) => ({
  selectedPreset: store.selectedPreset,
  prependContentFile: store.prependContentFile,
  updateContentFile: store.updateContentFile,
  removeContentFiles: store.removeContentFiles,
  showToast: store.showToast,
});

/** Main hook that orchestrates the generation workflow */
const useGenerate = () => {
  const { isAuthorized } = useAuth();
  const {
    selectedPreset,
    prependContentFile,
    updateContentFile,
    removeContentFiles,
    showToast,
  } = useAppStore(selector);
  const { submit } = useGenerationSubmit();
  const { poll } = useGenerationPolling();

  const generate = useCallback(async () => {
    if (!selectedPreset) return;
    if (!isAuthorized) {
      openRegistrationModal();
      return;
    }

    const {
      type,
      category = '',
      slug = '',
      title = '',
      flowId,
      presetCreditCount,
    } = selectedPreset;
    const settings = {
      category,
      id: slug,
    };
    const startTime = Date.now();
    sendGenerateStartAnalytics(settings);

    // Snapshot webhook params for retry context
    const { inputs } = getAppStore();
    const webhookParams = buildParamsFromInputs(inputs);

    const webhookContext: WebhookContext = {
      flowId,
      presetTitle: title,
      presetType: type,
      webhookParams,
      presetSlug: slug,
      categorySlug: category,
    };

    // Create placeholder before async work so we can always clean up on error.
    const placeholderUuid = `generating-${Date.now()}`;
    const placeholder: ContentFile = {
      uuid: placeholderUuid,
      name: 'Generating...',
      url: '',
      isLoading: true,
      generationStartTime: startTime,
      presetType: selectedPreset.type,
      webhookContext,
      ...(slug && {
        attributes: { category: `slug:${slug}, category:${category}` },
      }),
    };
    prependContentFile(placeholder);

    const onError = (error: unknown) =>
      handleGenerationError({
        uuid: placeholderUuid,
        error,
        startTime,
        settings,
        updateContentFile,
        showToast,
      });

    try {
      const result = await submit({
        flowId,
        presetCreditCount,
        preferFreeQuota: isOnboardingSession(),
      });
      if (!result) {
        removeContentFiles([placeholderUuid]);
        return;
      }

      const { id, runId } = result;
      await saveGeneration({
        generationId: id,
        placeholderUuid,
        presetTitle: title,
        startedAt: startTime,
        presetType: type,
      });

      // Poll in background — generate() resolves after submit so the button stops loading.
      // The .catch() lets the polling code do its own error-state transition
      // without producing an unhandled rejection on this fire-and-forget chain.
      poll({
        generationId: id,
        placeholderUuid,
        presetTitle: title,
        runId,
      })
        .then(() => {
          sendGenerateSuccessAnalytics(Date.now() - startTime, settings);
        })
        .catch((error: unknown) => {
          clearGeneration(id).catch(() => {});
          onError(error);
        });
    } catch (error) {
      onError(error);
    }
  }, [
    selectedPreset,
    isAuthorized,
    prependContentFile,
    updateContentFile,
    removeContentFiles,
    showToast,
    submit,
    poll,
  ]);

  return {
    generate,
    uploadMedia,
  };
};

export default useGenerate;
